"use client";

import type React from "react";
import { z } from "zod";

import { SubmitButton } from "@/components/ui/submit-button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { HoursOfService, HoursOfServiceEvents } from "@/interfaces";
import { errorHandler, formatDateTime, secondsToTime } from "@/lib/utils";
import { hoursOfServiceService } from "@/services/api/hoursOfService";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

const eventSchema = z
  .object({
    type: z.string().min(1, "Tipo é obrigatório"),
    start_date: z.string().min(1, "Data de início é obrigatória"),
    end_date: z.string().min(1, "Data de fim é obrigatória"),
  })
  .refine(
    (data) => {
      if (!data.start_date || !data.end_date) return true;
      return new Date(data.start_date) < new Date(data.end_date);
    },
    {
      message: "A data de início deve ser anterior à data de fim",
      path: ["end_date"],
    },
  );

interface DateTimeFieldProps {
  eventId: string | number;
  field: string;
  value: string;
  onChange: (eventId: string | number, field: string, value: string) => void;
  getFieldError: (eventId: string | number, field: string) => string;
  copiedDatetime: string;
  setCopiedDatetime: (value: string) => void;
}

const DateTimeField = ({
  eventId,
  field,
  value,
  onChange,
  getFieldError,
  copiedDatetime,
  setCopiedDatetime,
}: DateTimeFieldProps) => {
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <input
          type="datetime-local"
          step={1}
          className={`w-full p-2 border rounded dark:bg-gray-800 ${
            getFieldError(eventId, field) ? "border-red-500" : ""
          }`}
          value={value}
          onChange={(e) => onChange(eventId, field, e.target.value)}
        />
        <div className="flex shrink-0 ml-1">
          <button
            type="button"
            className="p-1 text-gray-500 hover:text-blue-500"
            onClick={() => {
              setCopiedDatetime(value);
              toast.success("Data copiada");
            }}
            title="Copiar data"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
          <button
            type="button"
            className="p-1 text-gray-500 hover:text-green-500"
            onClick={() => {
              onChange(eventId, field, copiedDatetime);
              toast.success("Data colada");
            }}
            title="Colar data copiada"
            disabled={!copiedDatetime}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={copiedDatetime ? "1" : "0.5"}
            >
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
            </svg>
          </button>
        </div>
      </div>
      {getFieldError(eventId, field) && (
        <p className="text-xs text-red-500">{getFieldError(eventId, field)}</p>
      )}
    </div>
  );
};

const HoursOfServiceReportForm = () => {
  const { id } = useParams<{ id: string }>();
  const [journeyData, setJourneyData] = useState<HoursOfService | null>(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<HoursOfServiceEvents[]>([]);
  const [newEventCount, setNewEventCount] = useState(0);
  const [reason, setReason] = useState("");
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [copiedDatetime, setCopiedDatetime] = useState("");

  const eventOptions = [
    "Direção",
    "Manobra",
    "Manutenção",
    "Carga",
    "Descarga",
    "Espera",
    "Abastecimento",
    "Refeição",
    "Descanso",
  ];

  useEffect(() => {
    const fetchJourneyData = async () => {
      try {
        const response = await hoursOfServiceService.show(Number(id));
        setJourneyData(response.data);
        setEvents(response.data.events || []);
      } catch (error) {
        errorHandler(error, "Erro ao carregar dados da jornada:");
      } finally {
        setLoading(false);
      }
    };

    fetchJourneyData();
  }, [id]);

  const handleAddEvent = () => {
    const newEventId = `new_${newEventCount}`;

    setEvents([
      ...events,
      {
        id: newEventId,
        hours_of_service_id: Number(id),
        start_date: "",
        end_date: "",
        type: "Direção",
        time: 0,
        isNew: true,
      },
    ]);

    setNewEventCount(newEventCount + 1);
    setValidationErrors({});
  };

  const handleRemoveEvent = (eventId: string | number) => {
    setEvents(events.filter((event) => event.id !== eventId));
  };

  const handleEventChange = (
    eventId: string | number,
    field: string,
    value: string,
  ) => {
    setEvents(
      events.map((event) => {
        if (event.id === eventId) {
          return { ...event, [field]: value };
        }
        return event;
      }),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (events.length === 0) {
      toast.error("Por favor, adicione ao menos um evento");
      return;
    }

    let hasErrors = false;
    const newErrors: Record<string, string> = {};

    events.forEach((event) => {
      try {
        eventSchema.parse({
          type: event.type,
          start_date: event.start_date,
          end_date: event.end_date,
        });
      } catch (error) {
        if (error instanceof z.ZodError) {
          error.issues.forEach((err) => {
            const fieldKey = `${event.id}_${String(err.path[0])}`;
            newErrors[fieldKey] = err.message;
            hasErrors = true;
          });
        }
      }
    });

    if (hasErrors) {
      setValidationErrors(newErrors);
      return;
    }

    setShowReasonModal(true);
  };

  const submitWithReason = async () => {
    if (!reason) {
      toast.error("Por favor, informe o motivo da edição");
      return;
    }

    const formatDate = (date: string) =>
      date.replace("T", " ").concat(":00").substring(0, 19);

    const newEvents = events
      .filter((event) => event.isNew)
      .map((event) => ({
        type: event.type,
        start_date: formatDate(event.start_date),
        end_date: formatDate(event.end_date),
      }));

    const oldEvents = events
      .filter((event) => !event.isNew)
      .map((event) => ({
        id: Number(event.id),
        type: event.type,
        start_date: formatDate(event.start_date),
        end_date: formatDate(event.end_date),
      }));

    try {
      const formData = {
        events: oldEvents,
        new_events: newEvents,
        reason,
      };

      await hoursOfServiceService.update(Number(id), formData);
      toast.success("Jornada atualizada com sucesso");
      window.location.reload();
    } catch (error) {
      errorHandler(error, "Erro ao atualizar jornada");
    }
  };

  const getFieldError = (eventId: string | number, field: string) => {
    const key = `${eventId}_${field}`;
    return validationErrors[key] || "";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">Carregando...</div>
    );
  }

  if (!journeyData) {
    return <div className="p-4 text-center">Nenhum dado encontrado.</div>;
  }

  return (
    <div className="overflow-auto p-4">
      <form onSubmit={handleSubmit}>
        <div className="overflow-x-auto mb-6">
          <Table className="min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="text-center dark:text-white">
                  Veículo
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Início
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Fim
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Intervalo interjornada
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Direção
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Carga/descarga
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Trabalho <br />
                  <span className="text-xs">(Hora normal)</span>
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Direção noturna
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Extra 50
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Extra 100
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Hora extra noturna 50
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Hora extra noturna 100
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Refeição
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Espera
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Espera noturna
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Descanso
                </TableHead>
                <TableHead className="text-center dark:text-white">
                  Total
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="text-center">
                <TableCell>{journeyData.vehicle.plate}</TableCell>
                <TableCell>{formatDateTime(journeyData.start_date)}</TableCell>
                <TableCell>{formatDateTime(journeyData.end_date)}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>{secondsToTime(journeyData.driving_time)}</TableCell>
                <TableCell>
                  {secondsToTime(journeyData.loading_unloading_time)}
                </TableCell>
                <TableCell>{secondsToTime(journeyData.working_time)}</TableCell>
                <TableCell>
                  {secondsToTime(journeyData.night_driving_regular_time)}
                </TableCell>
                <TableCell>
                  {secondsToTime(journeyData.extra_time_50 || 0)}
                </TableCell>
                <TableCell>
                  {secondsToTime(journeyData.extra_time_100 || 0)}
                </TableCell>
                <TableCell>
                  {secondsToTime(journeyData.night_overtime_50 || 0)}
                </TableCell>
                <TableCell>
                  {secondsToTime(journeyData.night_overtime_100 || 0)}
                </TableCell>
                <TableCell>{secondsToTime(journeyData.eating_time)}</TableCell>
                <TableCell>{secondsToTime(journeyData.waiting_time)}</TableCell>
                <TableCell>
                  {secondsToTime(journeyData.night_waiting_time)}
                </TableCell>
                <TableCell>{secondsToTime(journeyData.resting_time)}</TableCell>
                <TableCell>{secondsToTime(journeyData.total_time)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="overflow-x-auto mb-6 max-w-4xl mx-auto dark:text-white">
          <Table className="w-full max-w-4xl">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Ações</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Fim</TableHead>
                <TableHead>Duração</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    <button
                      type="button"
                      className="bg-red-500 text-white p-1 rounded"
                      onClick={() => handleRemoveEvent(event.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <select
                        className={`w-full p-2 border rounded dark:bg-gray-800 ${
                          getFieldError(event.id, "type")
                            ? "border-red-500"
                            : ""
                        }`}
                        value={event.type}
                        onChange={(e) =>
                          handleEventChange(event.id, "type", e.target.value)
                        }
                      >
                        {eventOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {getFieldError(event.id, "type") && (
                        <p className="text-xs text-red-500">
                          {getFieldError(event.id, "type")}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-56">
                    <DateTimeField
                      eventId={event.id}
                      field="start_date"
                      value={event.start_date}
                      onChange={handleEventChange}
                      getFieldError={getFieldError}
                      copiedDatetime={copiedDatetime}
                      setCopiedDatetime={setCopiedDatetime}
                    />
                  </TableCell>
                  <TableCell className="w-56">
                    <DateTimeField
                      eventId={event.id}
                      field="end_date"
                      value={event.end_date}
                      onChange={handleEventChange}
                      getFieldError={getFieldError}
                      copiedDatetime={copiedDatetime}
                      setCopiedDatetime={setCopiedDatetime}
                    />
                  </TableCell>
                  <TableCell>{secondsToTime(event.time)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  <button
                    type="button"
                    className="bg-blue-500 text-white px-3 py-2 rounded flex items-center mx-auto"
                    onClick={handleAddEvent}
                  >
                    <Plus size={16} className="mr-1" /> Novo evento
                  </button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="mt-6">
          <SubmitButton loading={loading} />
        </div>
      </form>

      {showReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 ">
          <div className="bg-white p-6 rounded-lg w-full max-w-md dark:text-white dark:bg-gray-800">
            <h3 className="text-lg font-bold mb-4">
              Por favor, informe o motivo da edição
            </h3>
            <textarea
              className="w-full p-2 border rounded mb-4 dark:bg-gray-800"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                className="bg-red-500 text-white px-4 py-2 rounded"
                onClick={() => setShowReasonModal(false)}
              >
                Cancelar
              </button>
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded"
                onClick={submitWithReason}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HoursOfServiceReportForm;
